/**
 * Naming ceremony decorations — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_decor_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const namingCeremonyDecorationsPack: ToilePack = {
    slug: 'naming-ceremony-decorations',
    category: 'occasions',
    name: 'Naming ceremony decorations',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'cradle', label: 'Cradle', subject: 'a flower-decorated cradle'},
        {key: 'backdrop', label: 'Backdrop', subject: 'a fabric wall drape with moons and stars'},
        {key: 'board', label: 'Name board', subject: 'a board with the baby\'s name reading "Aarav"'},
    ],
    styles: [
        {key: 'traditional', label: 'Traditional', style: 'traditional decoration with marigold and jasmine garlands, banana leaves and brass diyas, warm morning light from camera-left, a warm bright balance with soft shadows', palette: 'saffron, white and leaf green with brass', framing: 'eye-level view centered on the arrangement'},
        {key: 'pastel', label: 'Pastel', style: 'soft pastel decoration with balloon arches, paper moons and clouds, gentle daylight from overhead, an airy light balance with faint shadows', palette: 'baby blue, blush pink, lilac and cream', framing: 'straight-on view, the whole setup in frame'},
        {key: 'golden', label: 'Gold and white', style: 'elegant white and gold decoration with white roses, gold frames and sheer drapes, soft light from camera-right, a luminous balance with gentle shadow', palette: 'white, ivory and gold', framing: 'three-quarter view, the decoration filling most of the frame'},
    ],
    directions: ['baby girl', 'peacock theme', 'elephant theme'],
    directionHint: 'try: baby girl',
    seo: {
        title: 'Naming ceremony decoration: cradle, backdrop, name board',
        metaDescription: 'Nine naming ceremony decoration pictures for a namkaran at home: a cradle, a backdrop and a name board, in traditional, pastel and gold looks.',
        h1: 'Naming ceremony decoration: cradle, backdrop, name board',
        tagline: 'Nine pictures, three namkaran pieces, each in a traditional, a pastel and a gold and white look.',
        intro: 'Naming ceremony decoration for a namkaran at home: a flower-decorated cradle, a fabric backdrop with moons and stars, and a name board with a sample name. Each piece is shown in a traditional look of marigold and jasmine garlands with brass diyas, a pastel look of balloon arches and paper moons, and a gold and white look of white roses and sheer drapes. Type a twist, such as peacock theme or elephant theme, and the same pieces carry it.',
        useCases: [
            'a cradle decorated for a namkaran',
            'a backdrop behind the family for photos',
            'a name board for the ceremony',
            'a traditional or pastel setup for the day',
        ],
        faq: [
            {q: 'What is on the name board?', a: 'A sample name in letters, Aarav. The other pictures have no lettering.'},
            {q: 'What are the three looks?', a: 'Traditional, with marigold and jasmine garlands and brass diyas; pastel, with balloon arches and paper moons; and gold and white, with white roses and sheer drapes.'},
            {q: 'Can I ask for a peacock or elephant theme?', a: 'Yes. Type the theme, and the same three pieces are shown in it.'},
            {q: 'Are these real photographs?', a: 'No. They are generated pictures made to look like photographs. There are no people in them.'},
        ],
    },
}
